<script>

import TrainingTags from "@/components/trainings/TrainingTags.vue";
import TrainingInfo from "@/components/trainings/thumbnail/TrainingInfo.vue";
import TrainingSelectedDates from "@/components/trainings/thumbnail/TrainingSelectedDates.vue";

export default {
    name: 'TrainingCard',
    components: {TrainingSelectedDates, TrainingInfo, TrainingTags},
    props: {
        training: {
            type: Object,
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            showFullDescription: false,
            modalOpened: false
        }
    },
    computed: {
        canEdit() {
            const userId = this.$auth.user?.id;
            return this.training.user_id === userId || this.$auth.isAdmin();
        },
        canInvite() {
            return this.$auth.isEmployer() || this.$auth.isAdmin();
        },
        canEnroll() {
            return this.$auth.isEmployee() && !this.training.is_enrolled;
        },
        canOpen() {
            const isOwner = this.$auth.user && this.training.user_id === this.$auth.user.id;
            return this.training.is_enrolled || this.$auth.isEmployer() || this.$auth.isAdmin() || isOwner;
        },
        descriptionButtonText() {
            return this.showFullDescription ? this.$t('messages.show_less') : this.$t('messages.show_more');
        }
    },
    methods: {
        toggleFullDescription() {
            this.showFullDescription = !this.showFullDescription;
        },
        navigateToEdit() {
            if (this.training.is_series) {
                this.$router.push({name: 'training-series.edit', params: {id: this.training.id}});
            } else {
                this.$router.push({name: 'trainings.edit', params: {id: this.training.id}});
            }
        },
        navigateToDetails() {
            if (this.training.is_series) {
                this.$router.push({name: 'training-series.show', params: {id: this.training.id}});
            } else {
                this.$router.push({name: 'trainings.show', params: {id: this.training.id}});
            }
        },
        openEnrollModal() {
            this.$emit('onEnrollModalOpen', this.training);
        },
    }
}
</script>

<template>
    <article
        class="card training-card"
        :class="{ 'skeleton-block': loading, 'is-inactive': !training.is_active }"
    >
        <div class="series-badge-wrapper" v-if="training.is_series">
            <div class="series-badge has-background-success has-text-white">
                {{ $t('training_series.labels.series') }}
            </div>
        </div>
        <div class="card-content">
            <div class="control-buttons is-flex">
                <b-button
                    v-if="canEdit"
                    size="is-small"
                    icon-right="square-edit-outline"
                    :title="$t('trainings.labels.edit_training')"
                    @click="navigateToEdit"
                />
                <router-link
                    v-if="canInvite"
                    :to="{name: 'trainings.share', params: {id: training.id}}"
                    class="button is-small"
                    :title="$t('trainings.labels.share_with_employees')"
                >
                    <b-icon icon="share-variant-outline" size="is-small"/>
                </router-link>
            </div>

            <div class="media">
                <div class="media-content">
                    <p class="is-size-7 mb-2" v-if="!training.is_active">{{ $t('trainings.labels.training_inactive') }}</p>

                    <div class="thumbnail-top is-flex gap-2">
                        <div class="mb-2">
                            <TrainingTags :tags="training.tags" class="mb-2"/>
                            <TrainingInfo :training="training"/>
                        </div>
                        <div class="is-flex is-flex-direction-column is-align-items-flex-start">
                            <TrainingSelectedDates :training="training"/>
                        </div>
                    </div>
                </div>
            </div>

            <div class="content mt-4">
                <div
                    class="training-description"
                    :class="{ 'is-expanded': showFullDescription }"
                >
                    {{ training.description }}
                </div>
            </div>

            <footer class="buttons mt-4">
                <b-button @click="toggleFullDescription" class="is-ghost is-small">
                    {{ descriptionButtonText }}
                </b-button>

                <b-button type="is-primary" @click="openEnrollModal" v-if="canEnroll">
                    {{ $t('actions.enroll') }}
                </b-button>

                <b-button type="is-link" v-if="canOpen" @click="navigateToDetails">
                    {{ $t('actions.open') }}
                </b-button>
            </footer>
        </div>

    </article>
</template>

<style scoped lang="scss">
@use "bulma/sass/utilities/mixins";

.series-badge-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    width: 80px;      // rozmiar trójkąta
    height: 80px;
    overflow: hidden;
    z-index: 5;

    .series-badge {
        position: absolute;
        top: -35px;
        left: -35px;
        width: 70px;
        height: 70px;
        font-size: .6rem;
        text-align: center;
        padding: 3px 0;
        transform: rotate(-45deg);
        transform-origin: center center;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        text-transform: uppercase;
        letter-spacing: 1.5px;
    }
}

.thumbnail-top {
    flex-direction: column;
    justify-content: space-between;
    padding-right: 35px;

    @include mixins.desktop {
        flex-direction: row;
    }
}

.training-card {
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;

    &.is-inactive {
        opacity: 0.6;
        transition: opacity 0.3s ease;

        &:hover {
            opacity: 1;
        }
    }
}

.control-buttons {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    z-index: 10;
    gap: 4px;
}

.training-description {
    white-space: pre-line;
    max-height: 3em;
    overflow: hidden;
    line-height: 1.5;

    &.is-expanded {
        max-height: none;
    }
}
</style>
